import React, { ReactElement, useCallback, useState } from 'react';
import { Button, ClickAwayListener, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { getWalletIconById, useConnection } from 'modules/auth';
import { cropString } from 'modules/common';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { globalTranslation, useTranslation } from 'modules/i18n';
import { ArrowDownIcon } from 'modules/icons';

export function Account(): ReactElement {
  const { keys, t } = useTranslation(globalTranslation);
  const { classes, cx } = useStyles();
  const { disconnect, data } = useConnection();

  const [open, setOpen] = useState(false);

  const { onOpen } = useDialog(KnownDialogs.connect);

  const handleDisconnect = useCallback(async () => {
    await disconnect();
  }, [disconnect]);

  if (!data?.address) {
    return (
      <Button color="secondary" onClick={onOpen}>
        {t(keys.common.connectWallet)}
      </Button>
    );
  }

  const WalletIcon = data?.walletId && getWalletIconById(data?.walletId);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <Tooltip
          disableFocusListener
          disableHoverListener
          disableTouchListener
          arrow={false}
          classes={{ tooltip: classes.tooltip }}
          open={open}
          PopperProps={{ disablePortal: true }}
          title={
            <Button
              className={classes.disconnect}
              color="secondary"
              onClick={handleDisconnect}
            >
              {t(keys.common.disconnect)}
            </Button>
          }
          onClose={() => setOpen(false)}
        >
          <div className={classes.action} onClick={() => setOpen(!open)}>
            {WalletIcon && <WalletIcon className={classes.walletIcon} />}

            {cropString(data?.address)}

            <ArrowDownIcon
              className={cx(classes.icon, open && classes.openIcon)}
            />
          </div>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
}

export const useStyles = makeStyles()(theme => ({
  tooltip: {
    background: 'transparent',
  },
  action: {
    padding: theme.spacing(1, 4),
    height: 36,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    cursor: 'pointer',
    fontSize: 14,
    border: `2px solid ${theme.palette.background.default}`,
    borderRadius: 8,
    backgroundColor: theme.palette.primary.contrastText,

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
    },
  },
  disconnect: {
    fontSize: 14,
    marginTop: theme.spacing(-2),

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(-4),
    },
  },

  walletIcon: {
    width: 20,
  },
  icon: {
    transition: 'all 0.2s ease-in-out',
  },
  openIcon: {
    transform: 'rotate(180deg)',
  },
}));
